/**
 * Modal para cambiar contraseña de usuario
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../../components/UI/Modal';
import { userService } from '../../services';

const UserPasswordModal = ({ user, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
    forceChange: false
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });

  // Limpiar formulario cuando se abre/cierra el modal
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        newPassword: '',
        confirmPassword: '',
        forceChange: false
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo si existe
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Debe confirmar la nueva contraseña';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const passwordData = {
        newPassword: formData.newPassword,
        forceChange: formData.forceChange
      };

      const result = await userService.changeUserPassword(user.id, passwordData);

      if (result.success) {
        onSuccess?.();
        onClose();
      } else {
        // Manejar errores específicos
        if (result.validationErrors) {
          setErrors(result.validationErrors);
        } else {
          setErrors({ general: result.message });
        }
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setErrors({ general: 'Error cambiando la contraseña' });
    } finally {
      setSubmitting(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '#e0e0e0' };
    
    let strength = 0;
    const checks = [
      password.length >= 8,
      /[a-z]/.test(password),
      /[A-Z]/.test(password),
      /\d/.test(password),
      /[^a-zA-Z\d]/.test(password)
    ];
    
    strength = checks.filter(check => check).length;
    
    const levels = {
      0: { label: 'Muy débil', color: '#dc3545' },
      1: { label: 'Débil', color: '#fd7e14' },
      2: { label: 'Regular', color: '#ffc107' },
      3: { label: 'Buena', color: '#20c997' },
      4: { label: 'Fuerte', color: '#28a745' },
      5: { label: 'Muy fuerte', color: '#198754' }
    };
    
    return { 
      strength: (strength / 5) * 100, 
      ...levels[strength] 
    };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  if (!user) return null;

  // La API entrega `full_name`; reconstruimos el nombre con respaldo a nombre/apellido o email
  const displayName =
    user.full_name ||
    `${user.nombre || ''} ${user.apellido || ''}`.trim() ||
    user.email ||
    'Usuario';
  const avatarLetter = displayName.charAt(0).toUpperCase();

  return (
    <Modal
      title={`Cambiar Contraseña - ${displayName}`}
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
    >
      <div className="password-modal">
        <form onSubmit={handleSubmit}>
          {/* Error general */}
          {errors.general && (
            <div className="alert alert-error mb-4">
              <i className="fas fa-exclamation-triangle"></i>
              {errors.general}
            </div>
          )}

          {/* Información del usuario */}
          <div className="user-info">
            <div className="user-avatar">
              {avatarLetter}
            </div>
            <div className="user-details">
              <h4>{displayName}</h4>
              <p>{user.email}</p>
            </div>
          </div>

          {/* Nueva contraseña */}
          <div className="form-group">
            <label htmlFor="newPassword" className="required">
              Nueva Contraseña
            </label>
            <div className="password-input">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                placeholder="Mínimo 8 caracteres"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('new')}
              >
                <i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            
            {/* Indicador de fortaleza */}
            {formData.newPassword && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div 
                    className="strength-fill"
                    style={{ 
                      width: `${passwordStrength.strength}%`,
                      backgroundColor: passwordStrength.color 
                    }}
                  ></div>
                </div>
                <span className="strength-label" style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
            )}
            
            {errors.newPassword && (
              <div className="invalid-feedback">{errors.newPassword}</div>
            )}
            
            <small className="form-text text-muted">
              Recomendamos al menos 8 caracteres con mayúsculas, minúsculas, números y símbolos
            </small>
          </div>

          {/* Confirmar contraseña */}
          <div className="form-group">
            <label htmlFor="confirmPassword" className="required">
              Confirmar Nueva Contraseña
            </label>
            <div className="password-input">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                placeholder="Repetir nueva contraseña"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            {errors.confirmPassword && (
              <div className="invalid-feedback">{errors.confirmPassword}</div>
            )}
          </div>

          {/* Opciones adicionales */}
          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="forceChange"
                name="forceChange"
                checked={formData.forceChange}
                onChange={handleChange}
                className="form-check-input"
              />
              <label htmlFor="forceChange" className="form-check-label">
                Forzar cambio de contraseña
                <small className="help-text">
                  El usuario deberá cambiar su contraseña en el próximo inicio de sesión
                </small>
              </label>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Cambiando...
                </>
              ) : (
                <>
                  <i className="fas fa-key"></i>
                  Cambiar Contraseña
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .password-modal {
          .user-info {
            display: flex;
            align-items: center;
            gap: var(--spacing-md);
            background: var(--color-gray-50);
            padding: var(--spacing-lg);
            border-radius: var(--border-radius);
            margin-bottom: var(--spacing-xl);

            .user-avatar {
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: var(--color-primary);
              color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: var(--font-size-lg);
              font-weight: var(--font-weight-semibold);
            }

            .user-details {
              h4 {
                margin: 0 0 var(--spacing-xs) 0;
                color: var(--text-primary);
                font-size: var(--font-size-lg);
              }

              p {
                margin: 0;
                color: var(--text-secondary);
                font-size: var(--font-size-sm);
              }
            }
          }

          .password-input {
            position: relative;

            .password-toggle {
              position: absolute;
              right: var(--spacing-md);
              top: 50%;
              transform: translateY(-50%);
              background: none;
              border: none;
              color: var(--text-muted);
              cursor: pointer;
              font-size: var(--font-size-sm);
              transition: var(--transition);

              &:hover {
                color: var(--text-primary);
              }
            }

            input {
              padding-right: 3rem;
            }
          }

          .password-strength {
            display: flex;
            align-items: center;
            gap: var(--spacing-sm);
            margin-top: var(--spacing-xs);

            .strength-bar {
              flex: 1;
              height: 4px;
              background: var(--color-gray-200);
              border-radius: 2px;
              overflow: hidden;

              .strength-fill {
                height: 100%;
                transition: all 0.3s ease;
              }
            }

            .strength-label {
              font-size: var(--font-size-xs);
              font-weight: var(--font-weight-medium);
              min-width: 80px;
              text-align: right;
            }
          }

          .help-text {
            display: block;
            margin-top: var(--spacing-xs);
            color: var(--text-muted);
            font-size: var(--font-size-xs);
          }

          .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-md);
            margin-top: var(--spacing-xl);
            padding-top: var(--spacing-lg);
            border-top: 1px solid var(--border-light);
          }
        }
      `}</style>
    </Modal>
  );
};

UserPasswordModal.propTypes = {
  user: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

export default UserPasswordModal;
